const STORAGE_KEY = "vitolier_age_verified";
const EXPIRY_DAYS = 30;

// Jurisdiction minimum ages (conservative defaults)
const JURISDICTION_AGES: Record<string, number> = {
  US: 21,
  GU: 21, // Guam
  VI: 21, // US Virgin Islands
  AS: 21, // American Samoa
  MP: 21, // Northern Mariana Islands
  PR: 21, // Puerto Rico
  // Most countries use 18
};
const DEFAULT_MIN_AGE = 18;

export function getMinimumAge(countryCode: string): number {
  return JURISDICTION_AGES[countryCode.toUpperCase()] ?? DEFAULT_MIN_AGE;
}

export function calculateAge(dob: Date): number {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export function isEligible(dob: Date, countryCode: string): boolean {
  return calculateAge(dob) >= getMinimumAge(countryCode);
}

interface VerificationRecord {
  verified: true;
  jurisdiction: string;
  timestamp: number;
  expiry: number;
}

export function getVerification(): VerificationRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record: VerificationRecord = JSON.parse(raw);
    if (Date.now() > record.expiry) {
      clearVerification();
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

export function setVerification(jurisdiction: string): void {
  const now = Date.now();
  const record: VerificationRecord = {
    verified: true,
    jurisdiction,
    timestamp: now,
    expiry: now + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  // Also set a cookie for SSR resilience
  document.cookie = `${STORAGE_KEY}=1; max-age=${EXPIRY_DAYS * 86400}; path=/; SameSite=Lax`;
}

export function clearVerification(): void {
  localStorage.removeItem(STORAGE_KEY);
  document.cookie = `${STORAGE_KEY}=; max-age=0; path=/`;
}

export function isVerified(): boolean {
  return getVerification() !== null;
}

/** Age declaration wording showing only the threshold for the chosen country. */
export function ageDeclarationText(countryCode: string): string {
  return `I confirm I am at least ${getMinimumAge(countryCode)} years old.`;
}
